import { useState } from "react";
import { useConfigStore } from "../../../../../store/configStore";
import { useEditorStore } from "../../../../../store/editorStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface MoveSkuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sku: any;
  fromSelectionId: string;
}

export function MoveSkuDialog({
  open,
  onOpenChange,
  sku,
  fromSelectionId,
}: MoveSkuDialogProps) {
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);

  const { templateConfig } = useConfigStore();
  const { moveSkuToSelection } = useEditorStore();

  const handleMove = () => {
    if (!selectedTargetId) return;

    moveSkuToSelection(sku.id, fromSelectionId, selectedTargetId);
    setSelectedTargetId(null);
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setSelectedTargetId(null);
    }
  };

  // 可选的目标 Selection（排除当前所在的 Selection）
  const availableSelections =
    templateConfig?.selection
      .filter((s) => s.id !== fromSelectionId)
      .sort((a, b) => a.playIndex - b.playIndex) || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>移动 SKU</DialogTitle>
          <DialogDescription>
            将{" "}
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {sku.id}
            </code>{" "}
            移动到其他 Selection
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label className="text-sm mb-2 block">选择目标 Selection</Label>
          <ScrollArea className="h-[200px] border rounded-md">
            <div className="p-2 space-y-1">
              {availableSelections.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  没有可移动的目标 Selection
                </p>
              ) : (
                availableSelections.map((sel) => (
                  <button
                    key={sel.id}
                    type="button"
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded text-left text-sm transition-colors",
                      selectedTargetId === sel.id
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted",
                    )}
                    onClick={() => setSelectedTargetId(sel.id)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{sel.name || sel.id}</span>
                      {sel.name && (
                        <span
                          className={cn(
                            "text-xs",
                            selectedTargetId === sel.id
                              ? "text-primary-foreground/70"
                              : "text-muted-foreground",
                          )}
                        >
                          ID: {sel.id}
                        </span>
                      )}
                    </div>
                    <Badge
                      variant={
                        selectedTargetId === sel.id ? "secondary" : "outline"
                      }
                      className="shrink-0"
                    >
                      {(sel.skuIds || []).length} SKU
                    </Badge>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            取消
          </Button>
          <Button
            type="button"
            onClick={handleMove}
            disabled={!selectedTargetId}
          >
            移动
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
